import { useMemo } from "react"
import type { UVInsertConfig } from "@shared/models/uv-inserts"
import type { SuggestedMessaging } from "@shared/models/uv-inserts/suggested-messaging"
import { getUvInsertChooserPlan } from "@shared/view/uv-inserts/uv-insert-chooser"

export type UvInsertChooserViewModelOptions = {
  config: UVInsertConfig
  tabIndex: number
  suggestedMessaging: SuggestedMessaging
  selectedThemeIndex: number
  selectedSuggestionGroupIndex: number
}

/**
 * Maps config + tab + suggested messaging into chooser panels; consumer supplies selection indices.
 */
export function useUvInsertChooserViewModel(options: UvInsertChooserViewModelOptions) {
  const {
    config,
    tabIndex,
    suggestedMessaging,
    selectedThemeIndex,
    selectedSuggestionGroupIndex,
  } = options

  const plan = useMemo(
    () =>
      getUvInsertChooserPlan({
        config,
        tabIndex,
        suggestedMessaging,
      }),
    [config, tabIndex, suggestedMessaging],
  )

  const themeItems = plan.themeItems
  const safeThemeIndex =
    themeItems && themeItems.length > 0
      ? Math.min(Math.max(selectedThemeIndex, 0), themeItems.length - 1)
      : 0
  const themeSubtitle =
    themeItems && themeItems[safeThemeIndex]
      ? themeItems[safeThemeIndex].title.replace(/^Text\s*\/\s*/i, "").trim() || "Design selected"
      : "Select a design"

  const groups = plan.suggestionGroups ?? []
  const safeGroupIndex =
    groups.length > 0 ? Math.min(Math.max(selectedSuggestionGroupIndex, 0), groups.length - 1) : 0
  const activeSuggestions = groups[safeGroupIndex]?.suggestions ?? []
  const suggestionSubtitle =
    groups.length > 0 ? (groups[safeGroupIndex]?.name ?? "") : ""

  return {
    plan,
    themeItems,
    themeSubtitle,
    safeThemeIndex,
    groups,
    safeGroupIndex,
    activeSuggestions,
    suggestionSubtitle,
  }
}
